<script lang="ts">
  import { xhr, toast } from "$app/utils"
  import { SUBSCRIPTION_ROLES_LIST } from "$app/constants"
  import { appStore } from "$app/store.svelte"
  import SubscriptionUsage from "./SubscriptionUsage.svelte"
  import RolePermissions from "./RolePermissions.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import { signupStore } from "$app/signupstore.svelte"

  interface Props {
    oncreate: (user: UserTypeSubs) => void
  }

  let { oncreate }: Props = $props()

  const INIT_FORM = { name: "", email: "", role: null, permission_level: "" }
  let form: {
    name: string
    email: string
    role: RoleType | null
    permission_level: string
  } = $state({ ...INIT_FORM })
  let saving = $state(false)
  let errors: Record<string, string[]> = $state({})

  function canUpdateStandard(): boolean {
    if (form.role) {
      const prod_role = SUBSCRIPTION_ROLES_LIST.find(
        (role) => role.id === form.role
      )
      if (!prod_role) return false
      return signupStore.availableSeatsFor(prod_role.product_role) > 0
    }
    return false
  }

  function canUpdateFree(): boolean {
    return appStore.users.length < 3
  }

  let canUpdate = $state(false)

  $effect(() => {
    if (
      form.role &&
      signupStore.subscription.subscription_type === "unlimited"
    ) {
      canUpdate = true
    } else {
      canUpdate =
        form.role && signupStore.subscription.subscription_type === "standard"
          ? canUpdateStandard()
          : canUpdateFree()
    }
  })

  async function createInvitedUser() {
    const data = {
      name: form.name,
      email: form.email,
      role: form.role
    }
    saving = true

    try {
      const resp = await xhr.post<UserType>(
        `/plans/${appStore.plan.id}/admin/members`,
        {
          member: data
        }
      )

      toast("The member has been Added.", "success")
      let user = {
        ...resp.data,
        edit: false,
        showDetail: false
      } as UserTypeSubs
      oncreate(user)
    } catch (e: any) {
      const data = e.response.data

      if (data.errors) {
        errors = data.errors

        if (data.errors.base) {
          toast(data.errors.base[0], "error")
        }
      } else {
        toast("There was an error adding the member", "error")
      }

      if (!e.response.data) {
        Honeybadger.notify(e)
      }
    } finally {
      saving = false
    }
  }
</script>

<div class="py-2 min-w-2xl">
  <div>
    <div class="grid grid-cols-1 md:grid-cols-5 gap-x-3 gap-y-4 mt-2">
      <div
        class="border border-gray-100 rounded-sm shadow-sm px-4 py-2 md:col-span-3"
      >
        <h3 class="text-lg font-bold text-gray-900 text-center">
          Add New Member
        </h3>
        <div class="grid grid-cols-3 gap-3 pt-4 justify-center items-center">
          <label
            for="username"
            class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2 text-right"
          >
            Full Name
          </label>
          <div class="col-span-2 max-w-lg flex rounded-sm shadow-sm">
            <div class="input-cont w-full" class:error={errors.name}>
              <input
                type="text"
                class="input"
                bind:value={form.name}
                data-name="user-name"
                required
              />
              <div class="msg">{errors.name && errors.name[0]}</div>
            </div>
          </div>
        </div>
        <div class="grid grid-cols-3 gap-3 pt-6 justify-center items-center">
          <label
            for="username"
            class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2 text-right"
          >
            Email
          </label>
          <div class="col-span-2 max-w-lg flex rounded-sm shadow-sm">
            <div class="input-cont w-full" class:error={errors.email}>
              <input
                type="email"
                class="input"
                data-name="user-email"
                bind:value={form.email}
                required
              />
              <div class="msg">{errors.email && errors.email[0]}</div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-3 gap-3 pt-6 justify-center items-start">
          <label
            for="role"
            class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2 text-right"
          >
            Role
          </label>
          <div class="col-span-2 max-w-lg flex rounded-sm shadow-sm">
            <div class="input-cont w-full" class:error={errors.role}>
              <select
                bind:value={form.role}
                class="w-80"
                data-name="user-role"
                required
              >
                <option value="">Select Role</option>
                {#each SUBSCRIPTION_ROLES_LIST as rol}
                  <option value={rol.id}>{rol.label}</option>
                {/each}
              </select>
              <div class="msg">{errors.role && errors.role[0]}</div>
            </div>
          </div>
        </div>

        <div class="w-full pt-5 pb-3 text-center">
          <button
            class="btn-rounded success"
            data-name="add-member-btn"
            disabled={saving || !canUpdate}
            onclick={() => createInvitedUser()}
          >
            {#if saving}
              Saving..
            {:else}
              Add new member
            {/if}
          </button>

          <div class="py-2">
            <SubscriptionUsage />
          </div>
        </div>
      </div>

      <div
        class="p-2 bg-zinc-50 border border-blue-100 rounded-sm shadow-sm md:col-span-2"
      >
        <RolePermissions role={form.role} />
      </div>
    </div>
  </div>
</div>
